import React, {FC, useEffect, useState} from 'react';

import {Plural, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useInterval} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import {applyCriticalUpdate} from '../../../../services/updater/criticalUpdate';
import {Banner, BannerTypesEnum} from '../../../common/banners/banner';
import {BannerAction} from '../../../common/banners/bannerAction';
import {BannerContent} from '../../../common/banners/bannerContent';
import {BannerContentTitle} from '../../../common/banners/bannerContentTitle';
import {BannerFooterActions} from '../../../common/banners/bannerFooterActions';
import {BannerPrioritiesEnum} from '../../../common/banners/bannersContext';

/*
 * Props.
 */

interface CriticalUpdateBannerProps {
  criticalUpdateReloadTime?: number;
}

/*
 * Component.
 */

export const CriticalUpdateBanner: FC<CriticalUpdateBannerProps> = (props) => {
  const theme = useUserTheme();
  const [secondsLeft, setSecondsLeft] = useState<number>(Number.MAX_SAFE_INTEGER);
  const [setInterval, clearInterval] = useInterval();
  useEffect(() => {
    if (!props.criticalUpdateReloadTime) {
      return;
    }

    if (secondsLeft <= 0) {
      clearInterval();
      return;
    }

    if (secondsLeft < Number.MAX_SAFE_INTEGER) {
      return;
    }

    const recalculateSecondsLeft = () => {
      if (!props.criticalUpdateReloadTime) {
        return;
      }

      setSecondsLeft(Math.max(0, Math.round((props.criticalUpdateReloadTime - Date.now()) / 1000)));
    };

    setInterval(recalculateSecondsLeft, 1000);
    recalculateSecondsLeft();
  }, [clearInterval, props.criticalUpdateReloadTime, secondsLeft, setInterval]);

  if (!props.criticalUpdateReloadTime || secondsLeft === Number.MAX_SAFE_INTEGER) {
    return null;
  }

  return (
    <Banner
      bannerType={BannerTypesEnum.MULTILINE}
      color={theme.palette.red.shade40}
      priority={BannerPrioritiesEnum.ERROR}
    >
      <BannerContent>
        <BannerContentTitle>
          <Trans id="banner-criticalupdate-title">Front will reload</Trans>
        </BannerContentTitle>
        {renderContent(secondsLeft)}
      </BannerContent>
      <BannerFooterActions>
        <BannerAction onClick={applyCriticalUpdate}>
          <Trans id="banner-criticalupdate-reload">Reload Now</Trans>
        </BannerAction>
        <BannerAction
          onClick={() =>
            openInBrowser('https://help.frontapp.com/t/6390wv/critical-updates-and-force-reloads')
          }
        >
          <Trans id="banner-criticalupdate-moreinformation">More Information</Trans>
        </BannerAction>
      </BannerFooterActions>
    </Banner>
  );
};

function renderContent(secondsLeft: number) {
  return (
    <Plural
      id="banner-criticalupdate-content"
      value={secondsLeft}
      _0={<>A critical update is available. Reloading now.</>}
      one={<>A critical update is available. Reloading in 1 second.</>}
      other={<>A critical update is available. Reloading in {secondsLeft} seconds.</>}
    />
  );
}
